import {
  CompaniesInfiniteSelectField,
  FloatingFormItem,
  InputDate,
} from '@/components/_common';
import { selectCpfCnpjEnum } from '@/core/domain/invoice/settings/Settings.types';
import { FormatType } from '@/core/domain/reports/movements/dailyMovement/dailyMovement.types';
import { useWorkOrderMigrationReport } from '@/core/domain/reports/movements/work-order-migration-consult/work-order-migration-consult.hooks';
import {
  WorkOrderMigrationDateFilterOption,
  WorkOrderMigrationForm,
  WorkOrderMigrationRequest,
  WorkOrderMigrationStatusFilterOption,
} from '@/core/domain/reports/movements/work-order-migration-consult/work-order-migration-consult.types';
import { useDefaultEmptyFieldError } from '@/hooks';
import useFileLoading, { FileType } from '@/hooks/useFileLoading';
import { ReportCallback } from '@/hooks/useReportGenerate';
import { useReportHandlers } from '@/hooks/useReportHandler';
import { licensePlateMask } from '@/utils/mask/commonMasks';
import { DateTimeFormat } from '@/utils/moment';
import { Button, Col, Form, Modal, ModalProps, Row, Select } from 'antd';
import { Moment } from 'moment';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import MaskedInput from 'react-text-mask';

type Props = ModalProps;

export default function MigrationConsultReportModal(props: Props) {
  const intl = useIntl();
  const [form] = Form.useForm<WorkOrderMigrationForm>();

  const dateFilter = Form.useWatch('dateFilter', form);
  const startDate = Form.useWatch('startDate', form);
  const endDate = Form.useWatch('endDate', form);

  const workOrderMigrationReport = useWorkOrderMigrationReport();

  const defaultEmptyFieldError = useDefaultEmptyFieldError();

  const { fileLoading, setFileLoading, setFileUnloading, FILE_FORMAT } =
    useFileLoading();

  useEffect(() => {
    if (!dateFilter) {
      form.setFieldsValue({
        startDate: undefined,
        endDate: undefined,
      });
    }
  }, [dateFilter, form]);

  function validateStartDate(value?: Moment) {
    if (!value || !endDate) return Promise.resolve();

    if (value.isAfter(endDate, 'day')) {
      return Promise.reject(
        new Error(`Deve ser menor ou igual a ${endDate.format('DD/MM/YYYY')}`),
      );
    }

    return Promise.resolve();
  }

  function validateEndDate(value?: Moment) {
    if (!value || !startDate) return Promise.resolve();

    if (value.isBefore(startDate, 'day')) {
      return Promise.reject(
        new Error(
          `Deve ser maior ou igual a ${startDate.format('DD/MM/YYYY')}`,
        ),
      );
    }

    return Promise.resolve();
  }

  const handleGenerateReport = async (
    format: FileType,
    callback?: ReportCallback,
  ) => {
    setFileLoading(format);

    try {
      const formValues = await form.validateFields();

      const request: WorkOrderMigrationRequest = {
        companyIds: formValues.companyIds,
        dateFilter: formValues.dateFilter,
        startDate: formValues.startDate?.format(DateTimeFormat),
        endDate: formValues.endDate?.format(DateTimeFormat),
        format: FILE_FORMAT[format] as FormatType,
        licensePlate: formValues.licensePlate,
        statusFilter: formValues.statusFilter,
      };

      const result = await workOrderMigrationReport.mutateAsync(request);
      callback?.(result);
    } finally {
      setFileUnloading(format);
    }
  };

  const { handleView, handlePdf } = useReportHandlers(
    'orcamentos_migrados',
    handleGenerateReport,
  );

  return (
    <Modal
      {...props}
      width={800}
      title="Detalhe do complemento"
      cancelText="Fechar"
      destroyOnClose
      footer={[
        <Button key="cancel" type="primary" ghost onClick={props.onCancel}>
          Cancelar
        </Button>,
        <Button
          key="viewReport"
          type="primary"
          onClick={handleView}
          loading={fileLoading?.REPORT_VIEW}
        >
          Visualizar
        </Button>,
        <Button
          key="pdf"
          type="primary"
          onClick={handlePdf}
          loading={fileLoading?.PDF}
        >
          Gerar PDF
        </Button>,
      ]}
    >
      <Form
        form={form}
        preserve={false}
        layout="vertical"
        initialValues={{
          selectCpfCnpj: selectCpfCnpjEnum.cpf,
        }}
      >
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'cashMovement.form.company',
              })}
              name="companyIds"
            >
              <CompaniesInfiniteSelectField
                mode="multiple"
                allowClear
                activityFilter
              />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={8}>
            <FloatingFormItem label="Situação" name="statusFilter">
              <Select
                options={WorkOrderMigrationStatusFilterOption}
                allowClear
              />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={8}>
            <FloatingFormItem
              name="licensePlate"
              label="Placa"
              getValueFromEvent={(e) => String(e.target.value).toUpperCase()}
              rules={[
                {
                  min: 1,
                  max: 7,
                  message: intl.formatMessage({
                    id: 'commons.form.errors.invalidLicensePlate',
                  }),
                },
              ]}
            >
              <MaskedInput
                className="ant-input"
                mask={licensePlateMask}
                guide={false}
              />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={8}>
            <FloatingFormItem label="Data de:" name="dateFilter">
              <Select options={WorkOrderMigrationDateFilterOption} allowClear />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={8}>
            <FloatingFormItem
              label="De:"
              name="startDate"
              rules={[
                defaultEmptyFieldError,
                { validator: (_, value) => validateStartDate(value) },
              ]}
            >
              <InputDate
                disabledDate={(date) =>
                  !!endDate && date.isAfter(endDate, 'day')
                }
              />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={8}>
            <FloatingFormItem
              label="Até:"
              name="endDate"
              rules={[
                defaultEmptyFieldError,
                { validator: (_, value) => validateEndDate(value) },
              ]}
            >
              <InputDate
                disabledDate={(date) =>
                  !!startDate && date.isBefore(startDate, 'day')
                }
              />
            </FloatingFormItem>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
